import React, { useEffect, useState, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import BasicSwitches from "../../components/BasicSwitches";
import EditIcon from "@mui/icons-material/Edit";
import { axiosInstance } from "../../axiosInstance";
import { notify } from "../../components/toastify";
import Date from "./Date";
import loader from "../../assets/loader.webp";
import Pagination from "../../components/Pagination";
import ConfirmModal from "../../components/ConfirmModal";
import CloseIcon from "@mui/icons-material/Close";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DownloadSalesModal from "./DownloadSalesModal";
import debounce from "lodash-es/debounce";

const ManageCompanies = () => {
  const [data, setData] = useState([]);
  const [singleData, setSingleData] = useState({});
  const [edittab, setEdittab] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  const [companyIdToDelete, setCompanyIdToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [searchWord, setSearchWord] = useState("");
  const [industries, setIndustries] = useState([]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchData(value);
      }, 500),
    [] // Empty dependency array ensures it's created only once
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchWord(value);
    debouncedSearch(value); // Call the debounced function
  };

  const handleDelete = (id) => {
    setCompanyIdToDelete(id);
    setIsModalOpen(true); // Open the confirmation modal
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const fetchData = async (page, data) => {
    try {
      const response = await axiosInstance.post(
        `/api/superadmin/companies?page=${page}&searchParam=${data}`,
        {},
      );
      setTotalPage(response?.data?.data?.last_page);
      setData(response?.data?.data?.data);
    } catch (err) {
      console.log("Error fetching data", err);
      notify(err.response.data.message);
    } finally {
      setLoading(false);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    }
  };

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await axiosInstance.get("api/industry");
        setIndustries(response.data?.data || []);
      } catch (error) {
        console.log("Error fetching industries:", error);
      }
    };
    fetchIndustries();
  }, []);

  const handleConfirmDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `/api/superadmin/companies/delete/${companyIdToDelete}`,
      );
      notify(response?.data?.message, "success");
      fetchData(currentPage, searchData);
    } catch (err) {
      console.log("Error deleting company", err);
      notify(err.response.data.message);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await axiosInstance.get(
        `/api/superadmin/companies/show/${id}`,
      );
      console.log(response?.data?.data);
      // force main to update
      setSingleData(response?.data?.data); // Populate singleData with company details
      setEdittab(true); // Set edit mode
    } catch (err) {
      notify(err.response.data.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSingleData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDateChange = (year, month, day) => {
    setSingleData((prevData) => ({
      ...prevData,
      license_duration: `${year}-${month}-${day}`,
    }));
  };

  const handleFileChange = (e) => {
    setSingleData((prevData) => ({
      ...prevData,
      company_logo: e.target.files[0],
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Log singleData before submission to check its content

    if (!singleData?.id) {
      notify("Company ID is missing!");
      return;
    }

    const keysToExtract = [
      "company_name",
      "company_email",
      "company_address",
      "company_number",
      "license_duration",
      "password",
      "company_logo",
    ];

    const newSingleData = Object.keys(singleData).reduce((result, key) => {
      if (keysToExtract.includes(key)) {
        result[key] = singleData[key];
      }
      return result;
    }, {});

    // Create FormData object
    try {
      const response = await axiosInstance.put(
        `/api/superadmin/companies/update/${singleData?.id}`, // Use PUT for update
        newSingleData,
        // { headers: { "Content-Type": "multipart/form-data" } }
      );
      notify(response.data.message, "success");

      // Reset edit mode and refresh data
      setEdittab(false);
      fetchData(currentPage, searchData);
    } catch (error) {
      notify(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  // Paginaion code
  const handleCurrentPage = (page, side) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    fetchData(currentPage, searchData);
  }, [currentPage, searchData]);
  // useEffect(() => {
  //   setCurrentPage(1);
  //   fetchData(currentPage, searchData);
  //   console.log(searchData);
  // }, [searchData]);

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <img src={loader} alt="loader" className="w-20" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between p-3">
        <h2 className="text-2xl">
          <PeopleOutlineIcon /> Manage Companies
        </h2>
        <input
          placeholder="Search for"
          type="text"
          name="bill_name"
          value={searchWord}
          onChange={handleSearch}
          className="border-2 border-gray-400 w-60 p-2 px-6"
        />
      </div>

      {edittab && (
        <form
          onSubmit={handleEditSubmit}
          className="p-6 bg-lightgray rounded-lg"
        >
          <div className="flex justify-end">
            <CloseIcon
              className="cursor-pointer text-white"
              onClick={() => setEdittab(false)}
            />
          </div>

          <h4 className="text-xl font-semibold mb-3 text-white">
            Company Details
          </h4>

          <div className="flex flex-col lg:flex-row gap-6 flex-wrap">
            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">Company Name</label>
              <input
                className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                placeholder="Company Name"
                type="text"
                name="company_name"
                value={singleData?.company_name || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">Company Email</label>
              <input
                className="border-2 border-gray-300 p-2 rounded-md focus:border-gray-500 focus:outline-none"
                placeholder="youremail@mail.com"
                type="email"
                name="company_email"
                value={singleData?.company_email || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">Phone Number</label>
              <input
                className="border-2 border-gray-300 p-2 rounded-md focus:border-gray-500 focus:outline-none"
                placeholder="Phone Number"
                type="tel"
                name="company_number"
                value={singleData?.company_number || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">Company Address</label>
              <input
                className="border-2 border-gray-300 p-2 rounded-md focus:border-gray-500 focus:outline-none"
                placeholder="Company Address"
                type="text"
                name="company_address"
                value={singleData?.company_address || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">Industry Type</label>
              <select
                className="border-2 border-gray-300 p-2 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                name="industry_type"
                value={singleData?.industry_type_id || ""}
                disabled
              >
                {industries.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col lg:w-[48%]">
              <label className="text-sm text-white mb-1">
                License Duration
              </label>
              <Date
                onDateChange={handleDateChange}
                existingDate={singleData?.license_duration}
              />
            </div>

            <div className="flex flex-col lg:w-[48%] text-white">
              <label className="text-sm mb-1">
                Company Logo (if available)
              </label>
              <input
                type="file"
                name="company_logo"
                accept="image/*"
                className="border border-gray-300 rounded-md"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <div className="flex justify-center mt-6 text-white font-medium text-xl p-3 bg-darkblue rounded-lg">
            <button type="submit" disabled={loading} className="w-full h-full">
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      )}

      {/* Table for displaying companies */}
      {!edittab && (
        <div>
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border-b border-gray-300">NAME</th>
                <th className="py-2 px-4 border-b border-gray-300">EMAIL</th>
                <th className="py-2 px-4 border-b border-gray-300">PHONE</th>
                <th className="py-2 px-4 border-b border-gray-300">ADDRESS</th>
                <th className="py-2 px-4 border-b border-gray-300 text-wrap">
                  LICENSE DURATION
                </th>
                <th className="py-2 px-4 border-b border-gray-300">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr
                  key={item?.id}
                  className="hover:bg-gray-100 border-b border-gray-300"
                >
                  <td className="py-2 px-4 text-wrap">{item?.company_name}</td>
                  <td className="py-2 px-4 text-center text-wrap">
                    {item?.company_email}
                  </td>
                  <td className="py-2 px-4 text-center text-wrap">
                    {item?.company_number}
                  </td>
                  <td className="py-2 px-4 text-center text-wrap">
                    {item?.company_address}
                  </td>
                  <td className="py-2 px-4 text-center text-wrap">
                    {item?.license_duration}
                  </td>
                  <td className="py-2 px-4 flex gap-2 justify-center items-center">
                    <EditIcon
                      onClick={() => handleEdit(item?.id)}
                      className="text-blue-600 cursor-pointer"
                    />
                    <DeleteIcon
                      onClick={() => handleDelete(item?.id)}
                      className="text-red-600 cursor-pointer"
                    />
                    <BasicSwitches
                      status={item?.is_active}
                      id={item?.id}
                      api="/api/superadmin/companies"
                      requestType="get"
                      instanceType={axiosInstance}
                    />
                    <DownloadSalesModal id={item?.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            onPageChange={handleCurrentPage}
            totalPage={totalPage}
            currentPage={currentPage}
            setIsNextLoading={setIsNextLoading}
            setIsPrevLoading={setIsPrevLoading}
            isPrevLoading={isPrevLoading}
            isNextLoading={isNextLoading}
          />
        </div>
      )}

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        text1="Confirm Delete"
        text2="Are you sure you want to delete the company?"
      />
    </div>
  );
};

export default ManageCompanies;
